import { z } from 'zod';

import { CustomerDeliveryChannelSchema } from './customer-delivery-channel.schema.js';

export const SERVICE_ORDER_ITEM_OPERATIONAL_EXECUTION_STATUSES = [
  'PLANNED',
  'IN_PROGRESS',
  'WAITING_PARTS',
  'DONE',
] as const;

export const TransitionServiceOrderItemExecutionBodySchema = z.object({
  executionStatus: z.enum(SERVICE_ORDER_ITEM_OPERATIONAL_EXECUTION_STATUSES),
});

export type TransitionServiceOrderItemExecutionBody = z.infer<
  typeof TransitionServiceOrderItemExecutionBodySchema
>;

export const TransitionServiceOrderItemExecutionResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  serviceOrderItemId: z.uuid(),
  executionStatus: z.enum(SERVICE_ORDER_ITEM_OPERATIONAL_EXECUTION_STATUSES),
});

export type TransitionServiceOrderItemExecutionResponse = z.infer<
  typeof TransitionServiceOrderItemExecutionResponseSchema
>;

export const SetServiceOrderItemCustomerDecisionBodySchema = z.object({
  decision: z.enum(['APPROVED', 'REJECTED']),
});

export type SetServiceOrderItemCustomerDecisionBody = z.infer<
  typeof SetServiceOrderItemCustomerDecisionBodySchema
>;

export const SetServiceOrderItemCustomerDecisionResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  serviceOrderItemId: z.uuid(),
  approvedByCustomer: z.boolean(),
  rejectedByCustomer: z.boolean(),
  executionStatus: z.string(),
});

export type SetServiceOrderItemCustomerDecisionResponse = z.infer<
  typeof SetServiceOrderItemCustomerDecisionResponseSchema
>;

export const RequestServiceOrderCustomerApprovalBodySchema = z.object({
  deliveryChannel: CustomerDeliveryChannelSchema.optional(),
});

export type RequestServiceOrderCustomerApprovalBody = z.infer<
  typeof RequestServiceOrderCustomerApprovalBodySchema
>;

export const RequestServiceOrderCustomerApprovalResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  notifiedItemIds: z.array(z.uuid()),
});

export type RequestServiceOrderCustomerApprovalResponse = z.infer<
  typeof RequestServiceOrderCustomerApprovalResponseSchema
>;

export const AssignServiceOrderMechanicBodySchema = z.object({
  assignedMechanicUserId: z.uuid(),
  replace: z.boolean().optional(),
});

export type AssignServiceOrderMechanicBody = z.infer<typeof AssignServiceOrderMechanicBodySchema>;

export const AssignServiceOrderMechanicResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  serviceOrderItemId: z.uuid(),
});

export type AssignServiceOrderMechanicResponse = z.infer<
  typeof AssignServiceOrderMechanicResponseSchema
>;

export const RemoveServiceOrderItemMechanicResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  serviceOrderItemId: z.uuid(),
  userId: z.uuid(),
});

export type RemoveServiceOrderItemMechanicResponse = z.infer<
  typeof RemoveServiceOrderItemMechanicResponseSchema
>;

export const AllocateServiceOrderWorkbayBodySchema = z.object({
  workbayId: z.uuid(),
});

export type AllocateServiceOrderWorkbayBody = z.infer<typeof AllocateServiceOrderWorkbayBodySchema>;

export const AllocateServiceOrderWorkbayResponseSchema = z.object({
  allocationId: z.uuid(),
});

export type AllocateServiceOrderWorkbayResponse = z.infer<
  typeof AllocateServiceOrderWorkbayResponseSchema
>;

export const ReleaseServiceOrderWorkbayResponseSchema = z.object({
  allocationId: z.uuid(),
});

export type ReleaseServiceOrderWorkbayResponse = z.infer<
  typeof ReleaseServiceOrderWorkbayResponseSchema
>;

export const SERVICE_ORDER_TIME_ENTRY_TYPES = ['WORK', 'PAUSE_GLOBAL'] as const;

export const StartServiceOrderTimeEntryBodySchema = z
  .object({
    entryType: z.enum(SERVICE_ORDER_TIME_ENTRY_TYPES),
    serviceOrderItemId: z.uuid().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.entryType === 'WORK' && value.serviceOrderItemId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'serviceOrderItemId is required for WORK entries',
        path: ['serviceOrderItemId'],
      });
    }
    if (value.entryType === 'PAUSE_GLOBAL' && value.serviceOrderItemId !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'serviceOrderItemId must be omitted for PAUSE_GLOBAL entries',
        path: ['serviceOrderItemId'],
      });
    }
  });

export type StartServiceOrderTimeEntryBody = z.infer<typeof StartServiceOrderTimeEntryBodySchema>;

export const StartServiceOrderTimeEntryResponseSchema = z.object({
  timeEntryId: z.uuid(),
});

export type StartServiceOrderTimeEntryResponse = z.infer<
  typeof StartServiceOrderTimeEntryResponseSchema
>;

export const CloseServiceOrderTimeEntryResponseSchema = z.object({
  timeEntryId: z.uuid(),
});

export type CloseServiceOrderTimeEntryResponse = z.infer<
  typeof CloseServiceOrderTimeEntryResponseSchema
>;
